import { AspectRatio, Box, Flex, Icon, Image, Text, useDisclosure } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import IconDown from '@/assets/icons/ic-chevron-down.svg';
import IconFullscreen from '@/assets/icons/ic-fullscreen.svg';
import IconPresentOn from '@/assets/icons/ic-present-on.svg';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { useAnalytics } from '@/context/Analytics';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource } from '@/lib/datx-jsonapi-react';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';

export interface ILecturePosterDropdownProps {
  onOpen?: () => void;
}

export const LecturePosterDropdown: FC<ILecturePosterDropdownProps> = ({ onOpen }) => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { change } = useQueryParams<{ posterExpanded: boolean }>({
    posterExpanded: StringParam,
  });

  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));

  const handleOpenPoster = () => {
    onClose();
    change({ posterExpanded: true }, { method: 'replace', shallow: true });

    analytics.track('library_video_poster_expand', {
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      event_id: lecture.eventId,
      lecture_id: lecture.id,
    });
  };

  return (
    <Flex bg="dropdownAlpha" backdropFilter="blur(75px)" borderRadius="base" direction="column">
      <Box as="button" h="50px" alignItems="center" px="4" display={{ base: 'none', md: 'flex' }} onClick={onToggle}>
        <Text as="span" fontWeight="bold">
          {t('newLecture.poster.dropdown.label')}
        </Text>
        <Icon as={IconDown} w="24px" h="24px" style={{ transform: `rotate(${isOpen ? 180 : 0}deg)` }} />
      </Box>
      <Box
        as="button"
        h="40px"
        alignItems="center"
        p="2"
        display={{ base: 'flex', md: 'none' }}
        onClick={handleOpenPoster}
      >
        <Icon as={IconPresentOn} w="24px" h="24px" />
      </Box>

      {isOpen && (
        <Box px="4" pb="4" display={{ base: 'none', md: 'block' }}>
          <Box as="button" position="relative" width="full" display="block" onClick={handleOpenPoster}>
            <Flex position="absolute" w="full" h="full" justifyContent="center" alignItems="center" zIndex="1">
              <Flex
                w="40px"
                h="40px"
                justifyContent="center"
                alignItems="center"
                bg="dropdownAlpha"
                borderRadius="full"
              >
                <Icon as={IconFullscreen} w="18px" h="18px" />
              </Flex>
            </Flex>
            <AspectRatio ratio={163 / 101}>
              <Image
                src={
                  lecture.smallPosterDocumentThumbnailUrl ||
                  lecture.smallPosterDocumentThumbnailExtractUrl ||
                  LECTURE_CARD_IMAGE_FALLBACK
                }
                fit="fill"
                width="full"
              />
            </AspectRatio>
          </Box>
        </Box>
      )}
    </Flex>
  );
};
